import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { API_URL, DCX_SESSION_EXPIRATION_DELAY, DCX_URL, RES_URL, VUE_APP_URL } from "@/App.settings";
import axios from 'axios';
import BaseObjectTypeEnum from "@/enums/BaseObjectTypeEnum";
import ImageItem from '@/types/fields/ImageItem';
import PinTypeEnum from '@/enums/PinTypeEnum';
import Pin from '@/types/FilterTree/Pin';
import TreeNode from '@/types/TreeNode';
import BaseNode from '@/types/BaseNode';
import OverlayEnum from '@/enums/OverlayEnum';
import { Getter } from 'vuex-class';
import MenuItem from '@/types/MenuItem';
import { Threshold } from '@/types/Commons/Thresholds';
import ConnectionMethod from '@/types/ConnectionMethod';
import { Cookies } from 'vue-cookies-ts';
import VueRouter from 'vue-router';
import FilterCriterias from '@/types/FilterTree/FilterCriterias';
import cloneDeep from 'lodash/cloneDeep';

/** Classe de fonctions utilitaires liées aux objets */
@Component
export class ObjectUtils extends Vue {
	/**
	 * Test si l'objectType en paramètre est un équipement ou non
	 * @param objectType Type d'objet à tester
	 * @returns True s'il s'agit d'un équipement. False dans le cas contraire.
	 *  */
	public IsEquipment(objectType: string): boolean {
		const regexp = new RegExp('^Equipment\_*');
		return (regexp.test(objectType));
	}

	/**
	 * Test si l'objectType en paramètre est un compteur ou non
	 * @param objectType Type d'objet à tester
	 * @returns True s'il s'agit d'un compteur. False dans le cas contraire.
	 *  */
	public IsMeter(objectType: string): boolean {
		const regexp = new RegExp('^Equipment\_.*Meter$');
		return (regexp.test(objectType));
	}

	/**
	 * Test si l'objectType en paramètre est un business object ou non
	 * @param objectType Type d'objet à tester
	 * @returns True s'il s'agit d'un business object. False dans le cas contraire.
	 *  */
	public IsBusinessObject(objectType: string): boolean {
		const regexp = new RegExp('^BO\_*');
		return (regexp.test(objectType));
	}


	/** 
	 * Suppression du noeud/objet sélectionné dans l'arborescence
	 * @param nodeId					Noeud à supprimer	
	 * @param selectParentNode			Indique si le noeud parent doit être sélectionné
	 * @param childSectionToDelete		Indique la section à supprimer dans les form Children
	 * @param tableFieldNameToDelete	Indique le TableField à supprimer dans les form Children
	 *  */
	protected async delete(nodeId, selectParentNode = false, childSectionToDelete = "", tableFieldNameToDelete = "") {
		await this.$bvModal.msgBoxConfirm(this.$t('ConfirmObjectDeletion').toString(), { // $t('ConfirmObjectDeletion') / Etes-vous sûr de vouloir supprimer définitivement cet objet ?
			// $t('DeletionObject') / Suppression objet // $t('Yes') / Oui // $t('No') / Non
			title: this.$t('DeletionObject').toString(), okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true, modalClass: 'text-center'
		}).then(value => {
			if (value) {
				this.$store.dispatch("ShowLoadingMask", this.$t('DeleteInProgress').toString());

				this.$store.dispatch("ObjectStore/Delete", { nodeId: nodeId, selectParentNode: selectParentNode, childSectionToDelete: childSectionToDelete, tableFieldNameToDelete: tableFieldNameToDelete })
					.then(x => {
						/** @todo Obligé de mettre à jour complètement l'arborescence pour avoir les bons LeftBound/RightBound  ==> voir si nécessaire d'optimiser avec SLavina */

						this.$bvToast.toast(this.$t('ObjectDeleteSuccess').toString(), { // $t('ObjectDeleteSuccess') / L'objet sélectionné a été supprimé avec succès.
							title: this.$t('DeletionObject').toString(), // $t('DeletionObject') / Suppression objet 
							autoHideDelay: 3000,
							variant: "success",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					})
					.catch(err => {
						console.error(err);
						this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / Une erreur s'est produite.
							title: this.$t('DeletionObject').toString(), // $t('DeletionObject') / "Suppression objet",
							autoHideDelay: 10000,
							variant: "danger",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					})
					.finally(() => {
						this.$store.dispatch("HideLoadingMask", {});
					});
			}
		});
	}
}

/** Classe commune pour l'attribution d'icones Bootstrap ou FontAwesome */
@Component
export class IconsUtils extends Vue {

	/** Retourne icone suivant extension fichier */
	protected getExtensionIcon(type: string){
		return this.extensionsIcons[type];
	}

	/** Map des icones d'extension de fichier */
	private extensionsIcons: IconMap = {
		'.pdf': 'file-pdf',
		'.docx': 'file-word',
		'.xlsx': 'file-excel',
		'.png': 'file-image',
		'.jpeg': 'file-image',
		'.csv': 'file-csv',
		'.zip': 'file-zipper'
	}
}

/** Interface mapping des icones */
interface IconMap {
	[name: string]: string
}

/** Classe de fonctions utilitaire de gestion de lien */
@Component
export class LinkUtils extends Vue {
	/** Permet d'obtenir le chemin complet faire une image */
	public AbsoluteLink(iconLink: string) {
		return VUE_APP_URL + iconLink;
	}

	public GenerateFile(file: any, name: string){
		// console.debug('File : ', new Blob([file]));
		const url = window.URL.createObjectURL(new Blob([file]));
		//const url = window.URL.createObjectURL(file);

		console.debug('url : ', url);

		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', name);
		console.debug('link : ', link);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
}

/** Classe utilitaire de manipulation d'un carroussel d'images dans un composant Vue */
@Component
export class PicturesUtils extends Vue {
	/** Pour pouvoir utiliser RES_URL dans la section template */
	protected RES_URL = RES_URL;

	//#region Configuration
	/** Fichier image */
	protected imageItemId:string = "";
	/** Fichier image. Pas de type spécifié car il n'est pas possible de mettre null sur un type File pour le réinitialiser */
	protected imgFile = null;
	protected imgDescription: string = "";
	protected modifyMode: boolean = false;
	//#endregion Configuration

	/** Liste des ImageItem à traiter. Il n'est géré qu'une seule section avec un ImageField */
	protected imageItems: ImageItem[] = [];

	/** Index de l'image active dans le b-caroussel */
	protected currentImage: Number = 0;

	/** 
	 * Vérifie si le fichier passé est un bien un fichier image
	 * Types acceptés : "image/jpeg, image/png, image/gif, image/bmp, image/tiff"
	 **/
	@Watch('imgFile')
	protected checkFileType() {
		if(this.imgFile) {
			// Cast as unknown nécessaire pour éviter warning du compilateur TypeScript
			let file = this.imgFile as unknown as File;
			
			console.debug("PicturesUtils - file.type : ", file.type);

			if (file.type == "image/jpeg" || file.type == "image/png" || file.type == "image/gif" || file.type == "image/bmp" || file.type == "image/tiff")
				this.imgDescription = file.name.substring(0, file.name.lastIndexOf("."));
			else {
				this.$nextTick(() => {
					console.warn("PicturesUtils - checkFileType - Il ne s'agit pas d'un fichier image, réinit de imgFile");

					this.imgFile = null;
					this.imgDescription = "";

					this.$bvToast.toast(this.$t('InvalidImage').toString(), { // $t('InvalidImage') / Le fichier sélectionné n'est pas un fichier image valide. Seuls les fichiers de type .jpg, .png, .gif, .bmp ou .tiff sont autorisés.", {
						title: this.$t('Error').toString(), // $t('Error') / "Erreur",
						autoHideDelay: 10000,
						variant: "danger",
						solid: true,
						appendToast: true,
						toaster: "b-toaster-top-center"
					});
				})
			}
		}
	}

	/** @todo: Décodage de l'image chargée en local pour affichage en prévisualisation */ 
	// @Watch('imgFile')
	// private imageSrc() { //newValue, oldValue
	// 	//if (newValue !== oldValue) {
	// 		if (imgFile) {
	// 			base64Encode(imgFile)
	// 				.then(value => {
	// 				//this.imageSrc = value;
	// 				return value;
	// 				})
	// 				.catch(() => {
	// 				//this.imageSrc = null;
	// 				return null;
	// 				});
	// 			} else {
	// 			//this.imageSrc = null;
	// 		return null;
	// 		}
	// 	//}
	// }

	/** 
	 * Update/envoi de l'image au serveur
	 * @param processImages Pointeur vers fonction de traitement des images prenant des données images en paramètres
	 **/
	protected async upload(processImages: (img: ImageItem) => Promise<any>) {
		if (this.imgFile) {
			let formData: FormData = new FormData();

			// Cast as unknown nécessaire pour éviter warning du compilateur TypeScript
			let file = this.imgFile as unknown as File;

			formData.append("token", Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "");
			formData.append('file', file, file.name);

			const config = {
				headers: { 
					'Content-Type': 'multipart/form-data'
				}
			}

			/** @todo Faire l'upload() et le processImages() (save) en une seule fois */
			// Upload de l'image sur le server
			await axios.post(API_URL + "object/Upload", formData, config).then((response) => {
				console.debug("PictureUtils - upload() - succès - response.data : ", response.data);

				// response.data doit contenir l'Url de l'image uploadée
				if (response && response.data) {	// && this.validURL(response.data)
					console.debug("PictureUtils - upload() - dans if- response.data : ", response.data);
					let img: ImageItem = response.data;
					/** Initialise la description avec le nom de l'image */
					img.Description = this.imgDescription;

					processImages(img).then(() => {
						// Place le carroussel sur la dernière image ajoutée. Attente de 4 secondes le temps que l'image soit chargée dans le caroussel et le Dom rafraichit.
						setTimeout(() => {
							this.$nextTick(() => {
								// DOM updated
								console.debug("PictureUtils - this.imageItems.length : ", this.imageItems.length);
								this.currentImage = this.imageItems.length - 1;
							})
						}, 5000);

						this.$bvToast.toast(this.$t('SaveSuccessImage').toString(), { // La photo sélectionnée a été téléversée avec succès
							title: this.$t('Information').toString(), // $t('Information') / Information
							autoHideDelay: 10000,
							variant: "info",
							solid: true,
							appendToast: true,
							toaster: "b-toaster-top-center"
						});
					});
				}
			})
			.catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / Une erreur s'est produite
					title: this.$t('AnErrorOccured').toString(), // $t('Error') / "Erreur",
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			})
			.finally(() => {
				this.imageItemId = "";
				this.imgFile = null;
				this.imgDescription = "";
				this.modifyMode = false;
			});
		}
		else
			this.$bvToast.toast(this.$t('SelectImageMessage').toString(), { // $t('SelectImageMessage') / "Veuillez sélectionner un fichier image à ajouter.", {
				title: this.$t('UnableAddImage').toString(), // $t('UnableAddImage') / Ajout d'image impossible.
				autoHideDelay: 10000,
				variant: "warning",
				solid: true,
				appendToast: true,
				toaster: "b-toaster-top-center"
			});
	}

	/** 
	 * Affichage de la fenêtre modale d'ajout d'une image
	 * @param item : ImageItem à charger dans la modale pour modification
	 *  */
	protected editImg(item: ImageItem): Promise<any> {
		return new Promise(async (resolve, reject) => {
			if (item) {
				this.modifyMode = true;
				this.imageItemId = item.Id;
				//this.imgFile = item.Value;
				this.imgDescription = item.Description;
			}
			else {
				this.imageItemId = "";
				this.modifyMode = false;
				this.imgDescription = "";
			}
			resolve (true);
		});
	}

	/** Edition de l'imageItemId en cours (édité par la Modale) */
	protected editImgLst() {
		return new Promise(async (resolve, reject) => {
			console.debug("PicturesUtils - editImgLst() - this.imageFieldName : ", this.imageItemId);

			let index: number = this.imageItems.findIndex(x => x.Id === this.imageItemId);
			if (index > -1)
				this.imageItems[index].Description = this.imgDescription;

			resolve (true);
		});
	}

	/**
	 * Supprime une image d'imageItems
	 * @param imageId Id de l'image à supprimer
	 * @param processImages Fonction de traitement de l'image avant suppression effective d'imageItems (save, ...)
	 */
	protected removeImg(imageId: string, processImages: (arg?) => Promise<any>) {
		this.$bvModal.msgBoxConfirm(this.$t('ConfirmDeletePhoto').toString(), { // $t('ConfirmDeletePhoto') / Voulez-vous vraiment supprimer cette photo ? La suppression sera effective après la sauvegarde.
			title: this.$t('DeletePhoto').toString(), okVariant: 'danger', okTitle: this.$t('Yes').toString(), cancelTitle: this.$t('No').toString(), hideHeaderClose: false, centered: true
			}).then(value => {
				if (value) {
					// Enregistrement des parameters (listant les images) sur le widget
					processImages().then(() => {
						let index = this.imageItems.findIndex(x => x.Id === imageId);
						if (index > -1)
							this.imageItems.splice(index, 1);
					});
				}
			});
	}
}

/** Classe utilitaire de comparaison d'objets */
export class CompareUtils {
    /** 
	 * Supprime les propriétés spécifiées d'un objet (Helper interne)
	 * @param item Objet à manipuler
	 * @param ignoredProps Liste des propriétés à ignorer
	 * @example pour ignoredProps : 'metadata', 'users.*.secrets.pin' // Utilisation du joker * pour les tableaux
	 */
	private static ignoreProps(item: any, ignoredProps: string[]): any {
		if (!item || typeof item !== 'object') return item;

		// 1. Copie profonde
		const newItem = typeof structuredClone === 'function' 
			? structuredClone(item) 
			: JSON.parse(JSON.stringify(item));

		// 2. Fonction récursive pour naviguer et supprimer
		const removePath = (currentObj: any, keys: string[]) => {
			// Sécurité : si l'objet courant est null ou n'est pas un objet/tableau, on arrête
			if (!currentObj || typeof currentObj !== 'object') return;

			const currentKey = keys[0];

			// Condition d'arrêt : on est à la fin du chemin
			if (keys.length === 1) {
				delete currentObj[currentKey];
				return;
			}

			const remainingKeys = keys.slice(1);

			// Si la clé est le joker '*' et que l'objet courant est un tableau
			if (currentKey === '*' && Array.isArray(currentObj)) {
				// On applique la suite du chemin à CHAQUE élément du tableau
				currentObj.forEach(element => removePath(element, remainingKeys));
			} else {
				// Sinon, on continue la descente normale
				removePath(currentObj[currentKey], remainingKeys);
			}
		};

		// 3. Traitement de chaque chemin
		ignoredProps.forEach((path) => {
			removePath(newItem, path.split('.'));
		});

		return newItem;
	}

    /** Méthode de comparaison universelle (Objet ou Array) */
    public static EqualsParams(newVal: any, oldVal: any, excluded: string[] = []): boolean {
        if (!newVal || !oldVal) return newVal === oldVal;

        if (excluded.length > 0) {
            if (Array.isArray(newVal) && Array.isArray(oldVal)) {
                const newValMapped = newVal.map(item => this.ignoreProps(item, excluded));
                const oldValMapped = oldVal.map(item => this.ignoreProps(item, excluded));
                return JSON.stringify(newValMapped) === JSON.stringify(oldValMapped);
            } 
            
            const newValCleaned = this.ignoreProps(newVal, excluded);
            const oldValCleaned = this.ignoreProps(oldVal, excluded);

			console.debug("CompareUtils - EqualsParams() - JSON.stringify(newValCleaned) : %s - JSON.stringify(oldValCleaned) : %s", JSON.stringify(newValCleaned), JSON.stringify(oldValCleaned));
            
			return JSON.stringify(newValCleaned) === JSON.stringify(oldValCleaned);
        }
        return JSON.stringify(newVal) === JSON.stringify(oldVal);
    }

	/** Clone un objet */
	public static Clone(oldVal: any): any{
		// return JSON.parse(JSON.stringify(oldVal));
		return cloneDeep(oldVal); // Plus efficace de que le parsing json mais parfois mélange le contenu des collections (TODO: à revoir)
	}
}

/** 
 * Classe utilitaire de manipulation d'un VueGridLayout
 * @see https://jbaysolutions.github.io/vue-grid-layout/
 */
@Component
export class VueGridLayoutUtils extends Vue{
	/** 
	 * Notifie le VueGridItem de se redimensionner en fonction de la taille du WidgetContainer
	 * @param gridItem Object grid-item sur un composant VueGridLayout
	 * @see https://github.com/jbaysolutions/vue-grid-layout/issues/221
	 */
	protected async resizeGridItem(gridItem) {
		console.debug("WidgetUtils - resizeGridItem(%O) de type '%O'", gridItem, typeof gridItem);
		try {
			/** Débloque le minH et le maxH le temps d'appliquer l'autoSize() */
			gridItem._props.minH = 1;
			gridItem._props.maxH = Infinity;


			/** Redimensionne le VueGridItem en fonction de son contenu */
			return await gridItem.autoSize();
		}
		catch (e) {
			// Erreur de mutation interne au VueGridLayout sur utilisation autoResize (en attendant d'une meilleure solution)
			console.warn(e);
		}
	}

	/** Bloque le redimensionnement vertical d'un VueGridItem sur une certaine hauteur
	 * @param gridItem Object grid-item sur un composant VueGridLayout
	 * @param height Hauteur en nombre de lignes
	 */
	protected freezeGridItemHeight(gridItem, height) {
		try {
			gridItem._props.minH = height;
			gridItem._props.maxH = height;
		}
		catch (e){
			console.warn(e);
		}
	}
}

/** Classe utilitaire de manipulation du VueX FilterTree en-dehors de celui-ci */
export class FilterTreeUtils {

	/** 
	 * Créer la pin suivant un type d'objet
	 * @param objectType Type de la Pin à créer
	 **/
	public static SetPinNone(objectType: string, preview: boolean, maxPinId: number = 0): Pin{
		console.debug("FilterTreeUtils - SetPinNone() - type : %O - type.length : %O - (type && type.length == 0) : %O", objectType, objectType.length, objectType.length == 0);
		return { 
			Id: maxPinId++,
			Included: true, 
			Type: PinTypeEnum.None,
			ObjectType: BaseObjectTypeEnum.None,
			Reference: "",
			Selections: [], 
			Text: "", 
			Depreciated: false, 
			TreeNode: undefined,
			Filter : {
				ObjectType : objectType,
				Criterias : [],
				SubFilters : []
			}
		};
	}

	/** 
	 * Créer la pin suivant un type d'objet
	 * @param objectType Type de la Pin à créer
	 **/
	public static SetPinRoot(objectType: string, maxPinId: number = 0): Pin{
		console.debug("FilterTreeUtils - setPinType() - type : %O - type.length : %O - (type && type.length == 0) : %O", objectType, objectType.length, objectType.length == 0);
		return { 
			Id: maxPinId++,
			Included: true, 
			Type: PinTypeEnum.Root,
			ObjectType: BaseObjectTypeEnum.None,
			Reference: "",
			Selections: [], 
			Text: "", 
			Depreciated: false, 
			TreeNode: undefined,
			Filter : {
				ObjectType : objectType,
				Criterias : [],
				SubFilters : []
			} };
	}

	/** 
	 * Créer une Pin de type Tree à partir d'un noeud
	 * @description     Duplication de setPinTree présent dans FilterTreeUtils car pas possible d'utiliser la mixin dans un module VueX
	 * @param node      Noeud source
	 * @param preview   Indique si l'on est en mode prévisualisation ou non
	 **/
	public static SetPinTree(node: BaseNode | TreeNode, preview: boolean, maxPinId: number = 0): Pin {
	 	return {
	 		Id: maxPinId++,
	 		Included: true, 
	 		Type: PinTypeEnum.Tree, 
			ObjectType: node.ObjectType,
			Reference: node.Id,
	 		Selections: preview ? [] :[node.Id], 
	 		Text: node.Name, 
	 		Depreciated: preview ? false : node.Depreciated,  
			TreeNode: node as TreeNode,
	 		Filter : {} as FilterCriterias
		};
	}

	/** 
	 * Créer un noeud à partir d'une Pin
	 * @param pin Noeud source
	 *  */
	public static SetBaseNode(pin: Pin): BaseNode{
		return new BaseNode(pin.Reference, pin.Text, "", pin.ObjectType, "", pin.Depreciated);
	}

	/**
	 * Permet de raffraichir l'état du noeud
	 * @param isHistoricMode
	 * @param isCheckOnly
	 * @param node
	 * @returns
	 */
	public static UpdateNode(isHistoricMode: boolean, isCheckOnly: boolean, node: TreeNode) { 
		let selections = node.Selections;
		let historic: boolean = !(!node.Depreciated || (node.Depreciated && isHistoricMode));

		if (selections.length > 0) {
			/** Test si objet courant qui n'est pas inclus */
			let o0 = selections.findIndex(x => x.Affiliation === 0 && !x.Included) !== -1;
			/** Test si objet courant est inclus (s'il fait parti des checkleaders) */
			let o1 = selections.findIndex(x => x.Affiliation === 0 && x.Included) !== -1;
			/** Test s'il s'agit d'un ascendant qui n'est pas inclus */
			let p0 = selections.findIndex(x => x.Affiliation === 2 && !x.Included) !== -1;

			if (o0 && !o1 || o0 && o1) {
				node.Checked = false;
				node.CheckLeader = false;
				node.Indeterminate = false;
				node.NestedChecked = false;
				node.Disabled = false;
				node.Forced = false;
				node.Hidden = isCheckOnly || historic;
			} else if (!o0 && o1) {
				if (!historic) {
					node.Checked = true;
					node.CheckLeader = true;
					node.Indeterminate = p0;
					node.NestedChecked = false;
					node.Disabled = false;
					node.Forced = false;
					node.Hidden = historic;
				} else {
					node.Checked = true;
					node.CheckLeader = false;
					node.Indeterminate = false;
					node.NestedChecked = false;
					node.Disabled = false;
					node.Forced = false;
					node.Hidden = historic;
				}
			} else {
				/** Test si descendant pas inclus */
				let d0 = selections.findIndex(x => x.Affiliation === 1 && !x.Included) !== -1;
				/** Test si descendant inclus */
				let d1 = selections.findIndex(x => x.Affiliation === 1 && x.Included) !== -1;

				if (d0 && !d1 || d0 && d1) {
					node.Checked = false;
					node.CheckLeader = false;
					node.Indeterminate = false;
					node.NestedChecked = false;
					node.Disabled = true;
					node.Forced = false;
					node.Hidden = isCheckOnly || historic;
				} else if (!d0 && d1) {
					node.Checked = true;
					node.CheckLeader = false;
					node.Indeterminate = p0;
					node.NestedChecked = false;
					node.Disabled = node.ObjectType.includes('Meter');
					node.Forced = false;
					node.Hidden = historic;
				} else {
					/** Test si parent inclus */
					let p1 = selections.findIndex(x => x.Affiliation === 2 && x.Included) !== -1;

					if (p1) {
						node.Checked = false;
						node.CheckLeader = false;
						node.Indeterminate = false;
						node.NestedChecked = true;
						node.Disabled = false;
						node.Forced = isCheckOnly;
						node.Hidden = isCheckOnly || historic;
					} else {
						node.Checked = false;
						node.CheckLeader = false;
						node.Indeterminate = false;
						node.NestedChecked = false;
						node.Disabled = false;
						node.Forced = false;
						node.Hidden = isCheckOnly || historic;
					}
				}
			}
		} else {
			node.Checked = false;
			node.CheckLeader = false;
			node.Indeterminate = false;
			node.NestedChecked = false;
			node.Disabled = false;
			node.Forced = false;
			node.Hidden = isCheckOnly || historic;
		}
	}
}

/** Classe utilitaire de manipulation de chaines */
@Component
export class StringUtils extends Vue {
	/** 
	 * Met la première lettre d'une chaine en majuscule
	 * @param s Chaine à traiter
	 */
	protected capitalize(s) {
		return s[0].toUpperCase() + s.slice(1);
	}
}

/** Classe utilitaire de manipulation des breakpoints de l'écran */
@Component
export class ScreenUtils extends Vue {
	/** Test si l'écran est de petite taille et en portrait */
	protected checkSmPortrait() {
		return (this.$screen.breakpoint === 'xs' || this.$screen.breakpoint === 'sm') && this.$screen.portrait;
	}

	/** Test si l'écran est de petite taille et en paysage */
	protected checkSmLandscape() {
		return (this.$screen.breakpoint === 'xs' || this.$screen.breakpoint === 'sm') && this.$screen.landscape;
	}
}

/** Classe utilitaire de gestion des thèmes de couleurs de l'application */
@Component
export class ThemeUtils extends Vue {
	/** Génère les variables CSS à injecter dans la section style
	 * @param variant Variant de couleur Bootstrap à appliquer (présent dans default-theme.scss)
	 * @description Utilisé pour injecter les variants de couleurs Bootstrap (issue de default-theme.scss)
	 * @see default-theme.scss
	 */
	protected generateVariantStyle(variant: string) {
		return { '--Variant': 'var(--' + variant + ')', '--VariantLight': 'var(--' + variant + '-light)', '--VariantDark': 'var(--' + variant + '-dark)', '--VariantBgSubtle': 'var(--' + variant + '-bg-subtle)'};
	}
}

/** Classe utilitaire de manipulation de tableau (b-table-simple, b-table, ...) */
@Component
export class TableUtils extends Vue {
	protected sortEnum = SortEnum;

	/** Clé du champ en cours de tri */
	protected headerKeySorted: string = "";
	/** Ordre de tri à appliquer au prochain clic sur l'en-tête de colonnes headerKeySorted */
	protected nextSort: SortEnum = SortEnum.Desc;

	/**
	 * Tri des rows passés en paramètre selon une clé donnée
	 * @param headerKey 			Nom du champ qui sera a trié dans rows
	 * @param [rows=[]] 			Lignes à trier si null n'effectue pas de tri
	 * @param [mustRevertSort=true] Indique si l'on doit inverser le sens du tri
	 */
	protected sortColumn(headerKey: string, rows: any = [], mustRevertSort: boolean = true): void {
		console.debug("sortColumn - headerKey : ", headerKey);

		// Si l'entête de colonne à trier n'est pas celui qui a été trié précédemment, on revient à un ordre de tri ascendant par défaut
		if (this.headerKeySorted != headerKey)
			this.nextSort =  SortEnum.Asc;

		this.headerKeySorted = headerKey;

		if (this.nextSort == SortEnum.Asc) {
			rows.sort(function(a, b) {
				if (a[headerKey] > b[headerKey]) {
					return 1;
				}
				if (a[headerKey] < b[headerKey]) {
					return -1;
				}
				return 0;
			});
			if(mustRevertSort)
				/** Pour inverser le tri la prochaine fois */
				this.nextSort = SortEnum.Desc;
		}
		else if (this.nextSort == SortEnum.Desc) {
			rows.sort(function(a, b) {
				if (a[headerKey] < b[headerKey]) {
					return 1;
				}
				if (a[headerKey] > b[headerKey]) {
					return -1;
				}
				return 0;
			});
			if(mustRevertSort)
				/** Pour inverser le tri la prochaine fois */
				this.nextSort = SortEnum.Asc;
		}
	}
	
	/**
     * Regénère le tri par colonne en cours d'utilisation
     * @param rows liste des ligne à trier
     */
    protected regenCurrentSort(rows: any){
        console.debug("sortColumn - headerKey : ", this.headerKeySorted);
        const headerKey = this.headerKeySorted;

        /** regarde si il faut trier */
        if( headerKey !== ""){
                /** si le suivant est ascendant alors le current est descendant */
                if (this.nextSort == SortEnum.Asc) {
                
                rows.sort(function(a, b) {
                    if (a[headerKey] < b[headerKey]) {
                        return 1;
                    }
                    if (a[headerKey] > b[headerKey]) {
                        return -1;
                    }
                    return 0;
                });
            }
            /** sinon le suivant est descendant alors le current est ascendant */
            else if (this.nextSort == SortEnum.Desc) {
                rows.sort(function(a, b) {
                    if (a[headerKey] > b[headerKey]) {
                        return 1;
                    }
                    if (a[headerKey] < b[headerKey]) {
                        return -1;
                    }
                    return 0;
                });
            }
        }
    }
}

/** Ordres de tri possibles */
enum SortEnum {
	Asc = 0,
	Desc = 1
}

/** 
 * Classe utilitaire de gestion d'overlay de chargement
 * @typedef T Permet de spécifier une valeur d'enum à utiliser pour afficher l'overlay via displayOverlay
 **/
@Component
export class OverlayUtils<T = OverlayEnum> extends Vue {
	/** Détermine si un overlay est placé au-dessus de la fiche objet si aucun objet n'a été sélectionné */
	protected isOverlayDisplayed: boolean = false;
	protected displayedOverlay: T | OverlayEnum; // OverlayEnum.SingleNodeSelection;
	protected overlayOpacity: number = 1;

	/** 
	 * Méthode d'affichage du masque (overlay) de chargement
	 * @param typeOverlay Indique le type d'Overlay à afficher (à partir de l'enum OverlayEnum ou un enum étendu T hérité d'OverlayEnum)
	 * @param number Opacité à appliquer sur l'overlay
	 **/
	protected displayOverlay(typeOverlay: T | OverlayEnum, opacity: number = 1) {
		
		console.debug("OverlayUtils - displayOverlay(typeOverlay: " + typeOverlay + ", opacity: " + opacity + ")");

		this.isOverlayDisplayed = true;
		this.displayedOverlay = typeOverlay;
		this.overlayOpacity = opacity;
	}

	/** Cache le masque (overlay) de chargement */
	protected hideOverlay() {
		console.debug("hideOverlay())");
		this.isOverlayDisplayed = false;
	}
}


/** Classe utilitaire de gestion de la connexion */
@Component
export class ConnectionUtils extends Vue {
	/** Déterminer on est connecté à l'application ou non */
	protected isLoggedIn(): boolean {
		return this.$cookies.get('access_token') != null;
	}

	/** 
	 * Déconnexion 
	 * @param [redirect=true] Doit on rediriger l'utilisateur après sa deconnexion
	 */
	protected async logOut(redirect = true) {
		var data = {
			token: this.isLoggedIn() ? <string>this.$cookies.get('access_token') : ""
		};
		await axios({
			method: 'POST',
			url: API_URL + "connexion/logout",
			data: data,
			headers: {
				'Content-Type': 'application/json'
			}
		}).then(async response => {
			if (response && response.data) {
				localStorage.clear();
				this.$cookies.remove('access_token', {});

				// Retourne en arrière dans l'historique
				//this.$router.go(0);

				await this.$store.dispatch('AuthentificationStore/Load');
				const method: ConnectionMethod = this.$store.getters['AuthentificationStore/GetConnectionMethod'];

				// Réinitialisation des stores Vuex (via vuex-extensions) pour revenir à un état initial à la reconnexion
				// await (this.$store as StoreExtended<any>).reset({ self: true, nested: true, modules: { [MAIN_FILTER_TREE]: { self: true} } });
				// (this.$store. as StoreExtended<any>).reset({ self: true, nested: true })
				// Reset l'état de chaque module namespaced
				for(let name of Object.keys((this.$store as any)._modules.root._children)) {
					console.debug("ConnectionUtils - module name : ", name);

					if (this.$store.hasModule(name) ) 
						await this.$store.dispatch(name + "/Reset", {});
				}
				// Reset l'état des stores qui ne sont pas namespaced
				await this.$store.dispatch("Reset", {});

				/** @todo @AProuillac Rendre l'appel générique pour s'adapter en fonction de la méthode d'authentification (Strategy) */
				/** Permet de forcer la déconnexion SSO */
                if(this.$route.query.sso_force)
					await this.$store.dispatch('OidcStore/LogoutOidcIdp', method.oidcAuthority);

				/** Une fois déconnecter on force le navigateur à recomencer une ouverture neuve sur la page d'authentification */
				if(redirect)
					window.location.href = VUE_APP_URL;
			}
		})
		.catch(err => {
			console.error(err);
			this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / "Une erreur s'est produite."
				title: this.$t('LogOut').toString(), // $t('LogOut') / "Déconnexion" 
				autoHideDelay: 10000,
				variant: "danger",
				solid: true,
				appendToast: true,
				toaster: "b-toaster-top-center"
			});
		});
	}
}

/** Classe utilitaire de gestion des intéractions avec VueSync.aspx */
@Component
export class VueSyncHandler extends ConnectionUtils {
	/** Information du lien pour la iframe (utilisé pour appels V2) */
	protected iframeTarget = "";

	/** Information du nom pour la redirection de route */
	protected redirect: string= "";

	/** Indique si l'échange avec le VueSync s'est correctement passé ou non */
	protected vueSyncSuccess: boolean = false;

	/** Indique si un changement de base de données doit être éffectué (marche aussi sur le cas d'une reconnexion à la même base) */
	@Prop({default : () => false})
    public ChangeBase : boolean;

	/** 
	 * Gère la redirection si l'IFrame chargeant VueSync.aspx s'est correctement chargée (réception du message 'VueSyncLoaded')
	 * @param iK
	 * @param completeHandler Instructions à exécuter à l'issue de l'échange avec VueSync
	 * @see messageHandler
	 */
	protected async handleMessage(iK: string, access_token = undefined) {
		console.debug("VueSyncHandler - handleMessage() ");

		/** Gère la redirection vers /Master ou /Supervision avec le nouveau token */
		const redirect = async () => {
			if (access_token) {
				console.debug("VueSyncHandler - handleMessage() - redirect() - access_token : ", access_token);
				this.$cookies.set('access_token', access_token);
			}

			console.debug("VueSyncHandler - handleMessage() - redirect() : ", this.redirect);

			if(this.$route.path === this.redirect)
				this.$router.go(0);
			else
				// Redirection
				await this.$router.push(this.redirect);

			// Indique que l'échange avec VueSync s'est correctement passé et que l'utilisateur est donc loggé
			this.$store.dispatch("SessionStore/SetIsLoggin", true);
		}

		/** Gestion du message obtenu du VueSync. Redirection si le message est 'VueSyncLoaded' */
		const messageHandler = async (message) => {
			console.debug("VueSyncHandler - handleMessage() - iFrameTarget : %O - redirect : %O - e : %O", this.iframeTarget, this.redirect, message);
			if (this.iframeTarget != "" && message.data=='VueSyncLoaded') {
				this.vueSyncSuccess = true;
				// Suppression de l'écouteur d'événement
				window.removeEventListener('message', messageHandler);

				// Si changement de base, on change le access token et on recharge l'iFrame
				console.debug("VueSyncHandler - handleMessage() -  this.ChangeBase : %O - this.redirect : %O", this.ChangeBase, this.redirect);
				if(this.ChangeBase){
					// En cas de changement de base, on réalise une déconnexion.
					await this.logOut(false);
					await redirect();
				}
				else
					await redirect();
			}
		};

		Vue.set(this, "iframeTarget", DCX_URL + '/WebAPI/VueSync.aspx?lk=' + iK);
        await this.$store.dispatch('SessionStore/SetSync');

		this.$nextTick(() => {
			window.addEventListener('message', messageHandler);
		});

		/** On attend pendant 10 secondes la connexion au VueSync */
		setTimeout(() => {
			if(!this.vueSyncSuccess) {
				// Suppression de l'écouteur d'événement
				window.removeEventListener('message', messageHandler);

				/** Si on a pas obtenu le message 'VueSyncLoaded' de la part de VueSync.aspx */
				this.$bvToast.toast(this.$t('PleaseContactAdmin').toString(), { // $t('PleaseContactAdmin') / "Veuillez contacter votre administrateur. Vos droits sont peut-être échus.", {
					title: this.$t('UnableToConnect').toString(), // $t('UnableToConnect') / "Connexion impossible"
					autoHideDelay: 10000,
					variant: "warning",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
				this.$store.dispatch("HideLoadingMask", {});
			}
		}, 10000);
	}
}

/** 
 * Classe utilitaire d'accès aux variables d'environnement Vue stockées dans les fichiers .env.*
 * @see https://cli.vuejs.org/guide/mode-and-env.html
 **/
@Component
export class EnvUtils extends Vue {
	/** Nom de l'environnement */
	protected envName() {
		return process.env.VUE_APP_NAME;
	}

	/** Titre de l'environnement */
	protected title() {
		return process.env.VUE_APP_TITLE;
	}

	/** Version de l'environnement */
	protected version() {
		return process.env.VUE_APP_VERSION;
	}
}

/** Classe utilitaire de gestion des notes */
@Component
export class MarkUtils extends Vue {

	/**
	* Applique des classes CSS selon la valeur en paramètre
	* @param value
	*/
	protected changeMarkClass(value : number): string {
		let markClass: string = "";

		if(value)
			if (value > 8) 
				markClass = "bg-screamingGreen text-black";
			else if  (value > 6 && value <= 8)
				markClass = "bg-icterine text-black";
			else if  (value > 4 && value <= 6)
				markClass = "bg-princetonOrange text-black";
			else if (value > 2 && value <= 4)
				markClass = "bg-imperialRed text-white";
			else
				markClass = "bg-dimGray text-white";

		return markClass;
	}
}

/** Classe utilitaire gérant la langue des textes affichés dans le composant */
@Component
export class I18nUtils extends Vue {
    /** Langue des textes à afficher */
    @Prop({ default: () => 'fr-FR' })
    public Language!: string;

	/** Pour désactiver le watcher sur le changement de langue */
	protected unwatchLanguage = undefined;

	/** Gère le changment de la langue des textes affichés dans le composant en fonction de la prop Language */
	protected handleComponentLocale() {
		/** Watcher détectant le changement de langue pour l'appliquer dans toute l'application */
		this.unwatchLanguage = this.$watch('Language', (newVal, oldVal) => {
			this.$i18n.locale = newVal;
			console.log("I18nUtils - Changement de langue de '%s' vers '%s' - this.$i18n.locale : %s", oldVal, newVal, this.$i18n.locale);
		}, { immediate: true });
	}
}

/** Gestion menu contextuel */
@Component
export class ContextMenu extends Vue {
	//#region Gestion des filtres sur arbo en SelectModeEnum.MultiAdvanced
	/** Noeud qui a été cliqué (gauche ou droit) dans l'arborescence (et non nécessairement sélectionné) */
	protected currentNode: TreeNode = {} as TreeNode;
	//#endregion Gestion des filtres sur arbo en SelectModeEnum.MultiAdvanced

	// /** Détermine le type d'objet sélectionné pour filtrer */
	// protected objectType: string = BaseObjectTypeEnum.None;

	// /** Détermine si le noeud est modifiable/supprimable */
	// protected isEditable: boolean = true;

	/** Menu de création d'objets */
	@Getter('MenuStore/GetMenuItems')
	protected contextMenuItems!: MenuItem[];

	/**
	 * Gère le menu contextuel de l'arborescence
	 * @param e             Evènement
	 * @param x             Position du menu contextuel en abscisse
	 * @param y             Position du menu contextuel en ordonnée
	 * @param node          Noeud sélectionné
	 * @param openMenu		Indique si le menu doit être ouvert ou fermé
	 *  */
	protected contextMenuHandler(e, x, y, node: TreeNode, openMenu: boolean) {
		console.debug("contextMenuHandler - e : %O - x : %O - y : %O - node : %O - openMenu : %O", e, x, y, node, openMenu);
		const menu = (this.$refs as any).contextMenu.$refs.menu;

		if (menu) {
			if (node && openMenu) {
				menu.open();
				menu.setPosition(x, y, 'right');
				// this.objectType = node.ObjectType;
				// this.isEditable = node.IsEditable;
				this.currentNode = node;
			} 
			else
				menu.close(true);
		}

		e.preventDefault();
		e.stopPropagation();
	}

	/**
	 * Gère le menu contextuel de l'arborescence
	 * @param e             Evènement
	 * @description Ajouter un attribut propagation="false" un élément html permet de définir que le menu contextuel ne se ferme pas lors d'un clic sur cet item
	 *  */
	protected clearMenuHandler(e) {
		console.debug("clearMenuHandler - propagation : %O", e.target.attributes["propagation"])
		if (!e.target.attributes["propagation"]) {
			let menu = undefined;
			if ('contextMenu' in this.$refs)
				menu = (this.$refs as any).contextMenu.$refs.menu;

			if (menu)
				menu.close(true);
		}
	}
}

/** 
 * Classe utilitaire pour donner la couleur d'affichage d'un résultat suivant un ou des seuils définie a partir du composant Thresholds 
 * */
@Component
export class ThresholdUtils extends Vue {

	/** 
	 * Méthode pour donner la couleur d'affichage du résultat suivant seuils paramétrés 
	 * */
	public getThresholdColor(value: number, thresholds: Threshold[], defaultColor: string): string {
		let result = "";

		const threshold = thresholds.find(t => {
			const minValue = t.minValue ?? -Infinity;
			const maxValue = t.maxValue ?? Infinity;
			return value >= minValue && value <= maxValue;
		});
	
		result = threshold ? threshold.color.hex : defaultColor;
		
		return result;
	}
}

/** Classe utilitaire de gestion de la session */
export class SessionUtils {
	private cookies: Cookies;
	private router: VueRouter;

	public constructor(cookies: Cookies, router: VueRouter) {
		this.cookies = cookies;
		this.router = router;
	}

	/** Vérifie si la session est expirée et actualise le "timestamp" d'activité sinon */
	public async CheckExpirationSession(_e: MouseEvent){

		/** Récupère les informations du token */
		const token = this.cookies.get('access_token');
		const cookieTokenTimestamp = this.cookies.get('access_token_timestamp');
		
		/** Converti le timestamp récupéré en cookie vers Numeric */
		let tokenTimestamp = 0;
		if(cookieTokenTimestamp){
			tokenTimestamp = Number.parseInt(cookieTokenTimestamp.toString())
			if(isNaN(tokenTimestamp))
				tokenTimestamp = 0;
		}
	
		/** Regarde si l'on est dans le cadre d'une session */
		if(token){
			if(tokenTimestamp != 0){
				const timestamp = Date.now();
				/** Si supérieur à DCX_SESSION_EXPIRATION_DELAY */
				if((timestamp - tokenTimestamp) > DCX_SESSION_EXPIRATION_DELAY ) {
					this.cookies.set('access_token_status','expire');
					await this.router.push({ name: 'SignOut' }).catch((e) => e);;
				}
				else{
					this.cookies.set('access_token_timestamp', timestamp)
				}
			}else{
				this.cookies.set('access_token_status','expire');
				await this.router.push({ name: 'SignOut' }).catch((e) => e);;
			}
		}
	}

    public destroy() {
        this.cookies = null;
        this.router = null;
    }
}